import { assertEvent, assign, fromCallback, setup, sendTo } from "xstate";
import { enhancementActor } from "@design-lab/entities/artwork/model/enhancement.actor";
import type { Artwork } from "@design-lab/shared/types/artwork";
import {
  trackArtworkLongProcessing,
  trackImageEnhancementCompleted,
  trackImageEnhancementStarted
} from "@design-lab/shared/lib/analytics";
import type { models } from "jiffy-api";
import { createFormControl } from "react-hook-form";
import { transferTypesAtom } from "@design-lab/entities/transfer-types";
import { getDefaultStore } from "jotai";

export type EnhancementSource = "upload" | "design_library" | "canva" | "ai_generation";

export type ArtworkMachineState =
  | "initial"
  | "progress"
  | "uploaded"
  | "enhancing"
  | "warming"
  | "finished";

export type TransferFormData = {
  enhancementTier: models.cart.madeAdd.EnhancementTier;
  removeBackground: boolean;
  jiffyDesignServices: boolean;
  enhancementRequestNote?: string;
};

export type TransferFormControl = ReturnType<typeof createFormControl<TransferFormData>>;

// Transfer sizes form data structure - matches TransferLabForm from transferLabTypes
export type TransferSizesFormData = {
  artworks: Record<string, ArtworkSizesFormData>;
  artworkOrder: string[];
};

export type ArtworkSizesFormData = {
  id: string;
  name: string;
  url?: string;
  transferTypes: Record<string, TransferTypeFormData>;
  transferTypeOrder: string[];
};

export type TransferTypeFormData = {
  id: string;
  name: string;
  presets: Record<string, PresetFormData>;
  presetOrder: string[];
  customPreset: CustomPresetFormData;
};

export type PresetFormData = {
  id: string;
  name: string;
  sizes: Array<{
    id: string;
    size: string;
    width: number;
    height: number;
    quantity: number;
  }>;
};

export type CustomPresetFormData = {
  id: string;
  customSizes: Array<{
    id: string;
    width: number;
    height: number;
    quantity: number;
  }>;
};

export type TransferSizesFormControl = ReturnType<typeof createFormControl<TransferSizesFormData>>;

export type TransferArtworkMachineContext = {
  file?: File | undefined;
  artworkId?: number | undefined;
  artwork?: Artwork | undefined;
  startTime?: number | undefined;
  source?: EnhancementSource | undefined;
  lastWebsocketEvent?: string | undefined;
  cacheWarmingStatus: "started" | "warmed" | "none";
  visitedStates: ArtworkMachineState[];
  isReviewed: boolean;
  quantity: number;
  formControl: TransferFormControl;
  sizesFormControl: TransferSizesFormControl | undefined;
};

export const transferArtworkMachine = setup({
  types: {
    context: {} as TransferArtworkMachineContext,
    tags: {} as "loading",
    events: {} as
      | {
          type: "reset";
          context?: Partial<
            Omit<TransferArtworkMachineContext, "formControl" | "sizesFormControl">
          >;
          targetState?: "initial" | "finished";
        }
      | { type: "initial" }
      | { type: "upload.started"; file: File; source: EnhancementSource }
      | { type: "upload.completed"; id: number }
      | { type: "enhancement.started" }
      | { type: "enhancement.completed"; artwork: Artwork }
      | { type: "cache.warming.started" }
      | { type: "cache.warmed" }
      | { type: "finished" }
      | { type: "websocket.event"; name: models.cable.DtfChannel.Actions["action"] }
      | { type: "enhancement.completed"; artwork: Artwork }
      | { type: "progress.tick" }
      | { type: "review.mark" }
      | { type: "review.unmark" }
      | { type: "quantity.set"; value: number }
      | { type: "quantity.reset" }
  },
  actors: {
    progressTicker: fromCallback(({ sendBack }) => {
      const intervalId = setInterval(() => {
        sendBack({ type: "progress.tick" });
      }, 60_000);
      return () => clearInterval(intervalId);
    }),
    enhancement: enhancementActor
  },
  actions: {
    selectDesignTab: () => {
      // This action will be passed to the machine
    },
    highlightJiffyDesignService: () => {
      // This action will be passed to the machine
    },
    resetContext: assign(({ event, context }) => {
      assertEvent(event, "reset");

      const emptyContext: Omit<TransferArtworkMachineContext, "formControl" | "sizesFormControl"> =
        {
          file: undefined,
          artworkId: undefined,
          artwork: undefined,
          startTime: undefined,
          source: undefined,
          lastWebsocketEvent: undefined,
          cacheWarmingStatus: "none",
          visitedStates: [],
          isReviewed: false,
          quantity: 0
        };

      return {
        ...emptyContext,
        ...event.context,
        visitedStates: event.context?.visitedStates ?? [],
        formControl: context.formControl, // Preserve the form control
        sizesFormControl: context.sizesFormControl // Preserve the sizes form control
      };
    }),
    trackEnhancementStarted: ({ context }) => {
      if (context.artworkId !== undefined) {
        trackImageEnhancementStarted(
          String(context.artworkId),
          "process_dtf_artwork_started",
          context.source
        );
      }
    },
    initializeSizesForm: assign(({ context }) => {
      // Only initialize if not already initialized
      if (context.sizesFormControl) {
        return {};
      }

      // Generate a unique artwork ID (use artworkId if available, otherwise generate)
      const artworkId = context.artworkId ? context.artworkId.toString() : `temp-${Date.now()}`;
      const artworkName = (context.artwork as any)?.name || `Artwork ${artworkId}`;

      // Get transfer types from the global state
      const store = getDefaultStore();
      const transferTypesState = store.get(transferTypesAtom);
      const { defaultTransferTypeId } = transferTypesState.context;
      const defaultTransferType = transferTypesState.context.transferTypes.find(
        ({ id }) => id === defaultTransferTypeId
      );

      // Build initial transfer types with default if available
      const initialTransferTypes: Record<string, any> = {};
      if (defaultTransferTypeId && defaultTransferType) {
        initialTransferTypes[defaultTransferTypeId] = {
          id: defaultTransferTypeId,
          name: defaultTransferType.shortLabel,
          presets: {},
          presetOrder: [],
          customPreset: {
            id: `custom-${defaultTransferTypeId}`,
            customSizes: []
          }
        };
      }

      // Initialize with the multi-artwork structure but only containing this artwork
      // This maintains compatibility with existing lens-based forms
      const sizesFormControl = createFormControl<TransferSizesFormData>({
        mode: "onChange",
        defaultValues: {
          artworks: {
            [artworkId]: {
              id: artworkId,
              name: artworkName,
              url:
                context.artwork?.noBgUrl ||
                context.artwork?.noBgNoTrimUrl ||
                context.artwork?.originalImageUrl ||
                "",
              transferTypes: initialTransferTypes
            }
          },
          artworkOrder: [artworkId]
        }
      });
      return { sizesFormControl };
    }),
    trackEnhancementCompleted: ({ context }) => {
      if (context.artworkId !== undefined) {
        trackImageEnhancementCompleted({
          artworkId: String(context.artworkId),
          enhancementType: "process_artwork",
          startTime: context.startTime || 0,
          ...(context.source ? { source: context.source } : {})
        });
      }
    },
    logProgressTick: ({ context }) => {
      const { artworkId, startTime, source, lastWebsocketEvent, visitedStates } = context;
      const durationMs = Math.max(0, Date.now() - (startTime || 0));

      trackArtworkLongProcessing({
        artworkId: String(artworkId),
        startTime: startTime || 0,
        ...(source ? { source } : {}),
        durationMs,
        lastWebsocketEvent: lastWebsocketEvent || "none",
        passedSteps: visitedStates,
        cacheWarmingStatus: context.cacheWarmingStatus
      });
    }
  }
}).createMachine({
  context: () => ({
    visitedStates: [],
    cacheWarmingStatus: "none",
    isReviewed: false,
    quantity: 0,
    formControl: createFormControl<TransferFormData>({
      mode: "onChange",
      defaultValues: {
        enhancementTier: "full_enhancements",
        removeBackground: false,
        jiffyDesignServices: false,
        enhancementRequestNote: ""
      }
    }),
    // Sizes form is not initialized by default, only when artwork is uploaded
    sizesFormControl: undefined
  }),
  id: "artwork",
  initial: "initial",
  on: {
    reset: [
      {
        target: ".finished",
        guard: ({ event }) => event.targetState === "finished",
        actions: "resetContext"
      },
      {
        target: ".initial",
        actions: "resetContext"
      }
    ],
    "websocket.event": {
      actions: assign(({ event }) => {
        assertEvent(event, "websocket.event");
        return { lastWebsocketEvent: event.name };
      })
    },
    "progress.tick": {
      actions: "logProgressTick"
    },
    "review.mark": {
      actions: assign({ isReviewed: true })
    },
    "review.unmark": {
      actions: assign({ isReviewed: false })
    },
    "quantity.set": {
      actions: assign(({ event }) => {
        assertEvent(event, "quantity.set");
        return { quantity: Math.max(0, event.value) };
      })
    },
    "quantity.reset": {
      actions: assign({ quantity: 0 })
    }
  },
  states: {
    initial: {
      entry: assign(({ context }) => ({
        visitedStates: [...context.visitedStates, "initial"]
      })),
      on: {
        "upload.started": {
          target: "processing.progress",
          actions: assign({
            startTime: () => Date.now(),
            source: ({ event }) => event.source,
            file: ({ event }) => event.file
          })
        },
        "enhancement.started": {
          target: "processing.enhancing"
        }
      }
    },
    processing: {
      initial: "progress",
      invoke: [
        { src: "progressTicker" },
        {
          id: "enhancement",
          src: "enhancement",
          input: ({ context }) => {
            const input: any = {};
            if (context.file !== undefined) input.file = context.file;
            if (context.artworkId !== undefined) input.artworkId = context.artworkId;
            if (context.source !== undefined) input.source = context.source;
            return input;
          }
        }
      ],
      states: {
        progress: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "progress"]
          })),
          on: {
            "upload.completed": {
              target: "uploaded",
              actions: [
                assign({ artworkId: ({ event }) => event.id }),
                sendTo("enhancement", ({ event }) => event)
              ]
            }
          },
          tags: "loading"
        },
        uploaded: {
          entry: [
            assign(({ context }) => ({
              visitedStates: [...context.visitedStates, "uploaded"]
            })),
            "initializeSizesForm"
          ],
          on: {
            "enhancement.started": {
              target: "enhancing",
              actions: ["trackEnhancementStarted", sendTo("enhancement", ({ event }) => event)]
            },
            "enhancement.completed": {
              actions: [sendTo("enhancement", ({ event }) => event)]
            }
          },
          tags: "loading"
        },
        enhancing: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "enhancing"]
          })),
          on: {
            "cache.warming.started": {
              target: "warming",
              actions: [
                assign({ cacheWarmingStatus: "started" }),
                sendTo("enhancement", ({ event }) => event)
              ]
            },
            "enhancement.completed": {
              target: "#artwork.finished",
              actions: assign(({ event }) => {
                assertEvent(event, "enhancement.completed");
                return { artwork: event.artwork, file: undefined };
              })
            }
          },
          tags: "loading"
        },
        warming: {
          entry: assign(({ context }) => ({
            visitedStates: [...context.visitedStates, "warming"]
          })),
          on: {
            "cache.warmed": {
              actions: [
                assign({ cacheWarmingStatus: "warmed" }),
                sendTo("enhancement", ({ event }) => event)
              ]
            },
            "enhancement.completed": {
              target: "#artwork.finished",
              actions: assign(({ event }) => {
                assertEvent(event, "enhancement.completed");
                return { artwork: event.artwork, file: undefined };
              })
            }
          },
          tags: "loading"
        }
      }
    },
    finished: {
      entry: [
        assign(({ context }) => ({
          visitedStates: [...context.visitedStates, "finished"]
        })),
        // Initialize sizes form if not already initialized (e.g., for design library items)
        "initializeSizesForm"
      ]
    }
  }
});
